import React, { useState } from 'react';
import { IconButton } from '../IconButton/IconButton';
import { Tag } from '../Tag/Tag';
import styles from './ProcessTree.module.css';

export interface ProcessItemDto {
  id: string;
  name: string;
  steps: ProcessStepDto[];
}

export interface ProcessStepDto {
  id: string;
  operationId: string;
  type: number;
  rank: number;
  elements: any[];
}

interface ProcessTreeProps {
  /** Lista de itens do processo */
  items: ProcessItemDto[];
  /** Indica se a tela é apenas leitura */
  readOnly?: boolean;
  /** Estado de carregamento */
  loading?: boolean;
  /** Mensagem exibida quando não há itens */
  emptyState?: React.ReactNode;
  /** Conteúdo opcional para a barra de ferramentas */
  toolbar?: React.ReactNode;
  /** Callback para adicionar uma etapa a um item */
  onAddStep?: (item: ProcessItemDto) => void;
  /** Callback para editar um item */
  onEditItem?: (item: ProcessItemDto) => void;
  /** Callback para adicionar um elemento a uma etapa */
  onAddElement?: (item: ProcessItemDto, step: ProcessStepDto) => void;
  /** Callback para editar um elemento */
  onEditElement?: (element: any) => void;
  /** Callback para excluir um elemento */
  onDeleteElement?: (elementId: string) => void;
  /** Callback para excluir uma etapa */
  onDeleteStep?: (step: ProcessStepDto) => void;
  /** Callback para excluir um item */
  onDeleteItem?: (item: ProcessItemDto) => void;
  /** Função para obter o label de uma operação */
  getOperationLabel: (operationId: string) => string;
  /** Função para obter o rank de uma operação */
  getOperationRank?: (operationId: string) => number | undefined;
  /** Tipos de operação (mapa de labels) */
  operationTypeLabels: Record<number, string>;
  /** Classes CSS adicionais */
  className?: string;
}

export const ProcessTree: React.FC<ProcessTreeProps> = ({
  items,
  readOnly,
  loading,
  emptyState,
  toolbar,
  onAddStep,
  onEditItem,
  onAddElement,
  onEditElement,
  onDeleteElement,
  onDeleteStep,
  getOperationLabel,
  getOperationRank,
  operationTypeLabels,
  className = '',
}) => {
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const [expandedSteps, setExpandedSteps] = useState<Record<string, boolean>>({});

  const toggleItem = (id: string): void => {
    setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleStep = (id: string): void => {
    setExpandedSteps(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const renderElementBlock = (element: any): React.ReactNode => (
    <div key={element.id} className={styles.elementRow}>
      <div className={styles.elementInfo}>
        <i className="material-symbols-outlined">settings_input_component</i>
        <span className={styles.elementName}>{element.elementDescription}</span>
        {element.cause6mLabel && (
          <Tag label={element.cause6mLabel} severity="primary" />
        )}
      </div>
      {!readOnly && (
        <div className={styles.elementActions}>
          <IconButton
            icon="edit"
            variant="text"
            severity="secondary"
            aria-label="Editar elemento"
            onClick={() => onEditElement?.(element)}
          />
          <IconButton
            icon="delete"
            variant="text"
            severity="secondary"
            aria-label="Excluir elemento"
            onClick={() => onDeleteElement?.(element.id)}
          />
        </div>
      )}
    </div>
  );

  const renderStepBlock = (item: ProcessItemDto, step: ProcessStepDto): React.ReactNode => {
    const isExpanded = expandedSteps[step.id];
    const opLabel = getOperationLabel(step.operationId);
    const opRank = getOperationRank?.(step.operationId);
    const typeLabel = operationTypeLabels[step.type];

    return (
      <div key={step.id} className={styles.stepBlock}>
        <div className={styles.stepHeader} onClick={() => toggleStep(step.id)}>
          <div className={styles.stepTitleGroup}>
            <i className="material-symbols-outlined">
              {isExpanded ? 'expand_more' : 'chevron_right'}
            </i>
            <div className={styles.stepIdentity}>
              <span className={styles.stepName}>
                {opRank != null ? `${opRank}. ` : ''}{opLabel}
              </span>
              {typeLabel && <Tag label={typeLabel} severity="warning" />}
            </div>
          </div>
          <div className={styles.stepActions}>
            {!readOnly && (
              <>
                <IconButton
                  icon="add"
                  variant="text"
                  severity="success"
                  aria-label="Adicionar elemento"
                  onClick={(e) => { e.stopPropagation(); onAddElement?.(item, step); }}
                />
                <IconButton
                  icon="delete"
                  variant="text"
                  severity="secondary"
                  aria-label="Excluir etapa"
                  onClick={(e) => { e.stopPropagation(); onDeleteStep?.(step); }}
                />
              </>
            )}
          </div>
        </div>
        {isExpanded && (
          <div className={styles.elementList}>
            {step.elements && step.elements.length > 0 ? (
              step.elements.map(el => renderElementBlock(el))
            ) : (
              <div className={styles.emptyElements}>Nenhum elemento definido</div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderItemBlock = (item: ProcessItemDto): React.ReactNode => {
    const isExpanded = expandedItems[item.id];
    const hasSteps = (item.steps || []).length > 0;

    return (
      <div key={item.id} className={styles.itemCard}>
        <div className={styles.itemHeader} onClick={() => toggleItem(item.id)}>
          <div className={styles.itemTitleGroup}>
            <i className="material-symbols-outlined">
              {isExpanded ? 'expand_more' : 'chevron_right'}
            </i>
            <span className={styles.itemName}>{item.name}</span>
          </div>
          <div className={styles.itemActions}>
            {!readOnly && (
              <>
                {!hasSteps && (
                  <IconButton
                    icon="edit"
                    variant="text"
                    severity="secondary"
                    aria-label="Editar item"
                    onClick={(e) => { e.stopPropagation(); onEditItem?.(item); }}
                  />
                )}
                <IconButton
                  icon="add"
                  variant="text"
                  severity="primary"
                  aria-label="Adicionar etapa"
                  onClick={(e) => { e.stopPropagation(); onAddStep?.(item); }}
                />
              </>
            )}
          </div>
        </div>
        {isExpanded && (
          <div className={styles.stepList}>
            {item.steps && item.steps.length > 0 ? (
              item.steps.map(step => renderStepBlock(item, step))
            ) : (
              <div className={styles.emptySteps}>Nenhuma etapa vinculada</div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`${styles.container} ${className}`}>
      {toolbar && <div className={styles.toolbar}>{toolbar}</div>}
      
      <div className={styles.contentArea}>
        {loading ? (
          <div className={styles.loadingState}>
            <i className="material-symbols-outlined animate-spin">sync</i>
            <span>Carregando...</span>
          </div>
        ) : (items?.length || 0) === 0 ? (
          <div className={styles.emptyState}>{emptyState}</div>
        ) : (
          <div className={styles.treeGrid}>
            {items.map(renderItemBlock)}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProcessTree;
